<script lang="ts">
  // import { createEventDispatcher } from 'svelte';
  import type { DataPoint } from '../lib/types';
  import { formatTimestamp } from '../lib/utils';
  
  export let data: DataPoint[] = [];
  export let title: string = 'Data Points';
  export let pageSize: number = 10;
  export let showSearch: boolean = true;
  export let showPagination: boolean = true;
  export let highlightOutliers: boolean = false;
  
  // Not using dispatch currently, but keeping for future event handling
  // const dispatch = createEventDispatcher();
  
  // Sorting
  let sortField = 'timestamp';
  let sortDirection = 'desc';
  
  // Pagination
  let currentPage = 1;
  
  // Filtering
  let searchTerm = '';
  let filteredData: DataPoint[] = [];
  
  // Watch for data changes and update filtered data
  $: {
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredData = data.filter(item => {
        return (
          String(item.timestamp).toLowerCase().includes(searchLower) ||
          item.value.toString().includes(searchLower) ||
          (item.metadata && Object.values(item.metadata).some(val => 
            val && val.toString().toLowerCase().includes(searchLower)
          ))
        );
      });
    } else {
      filteredData = [...data];
    }
    
    // Apply sorting
    filteredData = sortData(filteredData, sortField, sortDirection);
  }
  
  // Calculate pagination values
  $: totalPages = Math.ceil(filteredData.length / pageSize);
  $: startIndex = (currentPage - 1) * pageSize;
  $: endIndex = Math.min(startIndex + pageSize, filteredData.length);
  $: paginatedData = filteredData.slice(startIndex, endIndex);
  
  // Function to calculate min/max values for outlier highlighting
  $: {
    const values = data.map(d => d.value);
    const mean = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    const stdDev = Math.sqrt(
      values.length 
        ? values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length 
        : 0
    );
    
    // Values more than 2 standard deviations from the mean are outliers
    const threshold = 2 * stdDev;
    const minNormal = mean - threshold;
    const maxNormal = mean + threshold;
    
    // Function to determine if a value is an outlier
    isOutlier = (value: number) => highlightOutliers && (value < minNormal || value > maxNormal);
  }
  
  // Helper function to check if a value is an outlier
  let isOutlier: (value: number) => boolean;
  
  // Sort data based on field and direction
  function sortData(data: DataPoint[], field: string, direction: string): DataPoint[] {
    if (field === 'timestamp') {
      return [...data].sort((a, b) => {
        const dateA = new Date(a.timestamp).getTime();
        const dateB = new Date(b.timestamp).getTime();
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      });
    } else if (field === 'value') {
      return [...data].sort((a, b) => {
        return direction === 'asc' ? a.value - b.value : b.value - a.value;
      });
    }
    return data;
  }
  
  // Handle sorting change
  function handleSort(field: string) {
    if (sortField === field) {
      sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortDirection = 'asc';
    }
  }
  
  // Handle pagination
  function goToPage(page: number) {
    currentPage = Math.max(1, Math.min(page, totalPages));
  }
  
  // Export data
  function exportCSV() {
    const headers = ['timestamp', 'value'];
    
    // Get all metadata keys
    const metadataKeys = new Set<string>();
    data.forEach(point => {
      if (point.metadata) {
        Object.keys(point.metadata).forEach(key => metadataKeys.add(key));
      }
    });
    
    // Add metadata headers
    const allHeaders = [...headers, ...Array.from(metadataKeys)];
    
    // Create CSV content
    let csvContent = allHeaders.join(',') + '\n';
    
    data.forEach(point => {
      const values = [
        point.timestamp,
        point.value.toString()
      ];
      
      // Add metadata values
      metadataKeys.forEach(key => {
        const value = point.metadata?.[key] ?? '';
        values.push(value.toString());
      });
      
      csvContent += values.join(',') + '\n';
    });
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'time_series_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
</script>

<div class="data-table-container">
  <div class="data-table-header mb-3 d-flex justify-content-between align-items-center">
    <h5 class="mb-0">{title} ({data.length} records)</h5>
    <div class="data-table-actions">
      <button class="btn btn-sm btn-outline-secondary me-2" on:click={exportCSV}>
        <i class="bi bi-download me-1"></i>
        Export CSV
      </button>
    </div>
  </div>
  
  {#if showSearch}
    <div class="data-table-search mb-3">
      <div class="input-group">
        <span class="input-group-text">
          <i class="bi bi-search"></i>
        </span>
        <input 
          type="text" 
          class="form-control" 
          placeholder="Search data..." 
          bind:value={searchTerm}
        />
        {#if searchTerm}
          <button 
            class="btn btn-outline-secondary" 
            type="button"
            on:click={() => searchTerm = ''}
          >
            <i class="bi bi-x"></i>
          </button>
        {/if}
      </div>
    </div>
  {/if}
  
  <div class="table-responsive">
    <table class="table table-hover">
      <thead>
        <tr>
          <th 
            on:click={() => handleSort('timestamp')}
            class="sortable-header {sortField === 'timestamp' ? 'sorted' : ''}"
          >
            Timestamp
            {#if sortField === 'timestamp'}
              <i class="bi {sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'}"></i>
            {/if}
          </th>
          <th 
            on:click={() => handleSort('value')}
            class="sortable-header {sortField === 'value' ? 'sorted' : ''}"
          >
            Value
            {#if sortField === 'value'}
              <i class="bi {sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'}"></i>
            {/if}
          </th>
          <th>Metadata</th>
        </tr>
      </thead>
      <tbody>
        {#if paginatedData.length === 0}
          <tr>
            <td colspan="3" class="text-center py-4">
              {searchTerm ? 'No matching records found' : 'No data available'}
            </td>
          </tr>
        {:else}
          {#each paginatedData as point}
            <tr class={isOutlier(point.value) ? 'table-warning' : ''}>
              <td>{formatTimestamp(point.timestamp)}</td>
              <td class={isOutlier(point.value) ? 'fw-bold' : ''}>
                {point.value.toFixed(2)}
                {#if isOutlier(point.value)}
                  <i class="bi bi-exclamation-triangle-fill text-warning ms-1" title="Outlier detected"></i>
                {/if}
              </td>
              <td>
                {#if point.metadata && Object.keys(point.metadata).length > 0}
                  {@const metadataId = `metadata-${Math.random().toString(36).substring(2, 9)}`}
                  <button 
                    class="btn btn-sm btn-outline-secondary metadata-toggle" 
                    data-bs-toggle="collapse" 
                    data-bs-target={`#${metadataId}`}
                    aria-expanded="false"
                  >
                    View Metadata
                  </button>
                  <div class="collapse mt-2" id={metadataId}>
                    <div class="metadata-display">
                      {#each Object.entries(point.metadata) as [key, value]}
                        <div><strong>{key}:</strong> {value}</div>
                      {/each}
                    </div>
                  </div>
                {:else}
                  <span class="text-muted">None</span>
                {/if}
              </td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
  
  {#if showPagination && totalPages > 1}
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div class="pagination-info">
        Showing {startIndex + 1} to {endIndex} of {filteredData.length} entries
      </div>
      <nav aria-label="Data table pagination">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item {currentPage === 1 ? 'disabled' : ''}">
            <button class="page-link" on:click={() => goToPage(1)} aria-label="First page">
              <i class="bi bi-chevron-double-left"></i>
            </button>
          </li>
          <li class="page-item {currentPage === 1 ? 'disabled' : ''}">
            <button class="page-link" on:click={() => goToPage(currentPage - 1)} aria-label="Previous page">
              <i class="bi bi-chevron-left"></i>
            </button>
          </li>
          
          {#each Array(Math.min(5, totalPages)) as _, i}
            {#if totalPages <= 5}
              <!-- If 5 or fewer pages, show all -->
              <li class="page-item {currentPage === i + 1 ? 'active' : ''}">
                <button class="page-link" on:click={() => goToPage(i + 1)}>{i + 1}</button>
              </li>
            {:else if currentPage <= 3}
              <!-- If near start, show first 5 -->
              {#if i < 5}
                <li class="page-item {currentPage === i + 1 ? 'active' : ''}">
                  <button class="page-link" on:click={() => goToPage(i + 1)}>{i + 1}</button>
                </li>
              {/if}
            {:else if currentPage >= totalPages - 2}
              <!-- If near end, show last 5 -->
              {#if i < 5}
                <li class="page-item {currentPage === totalPages - 4 + i ? 'active' : ''}">
                  <button class="page-link" on:click={() => goToPage(totalPages - 4 + i)}>{totalPages - 4 + i}</button>
                </li>
              {/if}
            {:else}
              <!-- If in middle, show current and 2 on each side -->
              {#if i < 5}
                <li class="page-item {i === 2 ? 'active' : ''}">
                  <button class="page-link" on:click={() => goToPage(currentPage - 2 + i)}>{currentPage - 2 + i}</button>
                </li>
              {/if}
            {/if}
          {/each}
          
          <li class="page-item {currentPage === totalPages ? 'disabled' : ''}">
            <button class="page-link" on:click={() => goToPage(currentPage + 1)} aria-label="Next page">
              <i class="bi bi-chevron-right"></i>
            </button>
          </li>
          <li class="page-item {currentPage === totalPages ? 'disabled' : ''}">
            <button class="page-link" on:click={() => goToPage(totalPages)} aria-label="Last page">
              <i class="bi bi-chevron-double-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  {/if}
</div>

<style>
  .data-table-container {
    width: 100%;
  }
  
  .sortable-header {
    cursor: pointer;
    user-select: none;
  }
  
  .sortable-header:hover {
    background-color: #f8f9fa;
  }
  
  .sortable-header.sorted {
    background-color: #e9ecef;
  }
  
  .metadata-display {
    background-color: #f8f9fa;
    padding: 0.5rem;
    border-radius: 4px;
    font-size: 0.875rem;
  }
  
  .metadata-toggle {
    padding: 0.15rem 0.5rem;
    font-size: 0.75rem;
  }
  
  .pagination-info {
    font-size: 0.875rem;
    color: #6c757d;
  }
</style>
